import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, Check, ArrowRight } from "lucide-react";
import { VbError, VbEmpty } from "@/components/vb";
import { VerdictSkeleton } from "./verdict/VerdictSkeleton";
import { useBuyer, useAnalysis } from "@/hooks/vb/useBuyers";
import type { AnalysisResultEnvelope, Big5Category } from "@/types/vb";
import { extractVbError } from "./buyer-create/errors";
import { buildVerdictModel } from "./verdict/verdictModel";
import { VerdictHeader } from "./verdict/VerdictHeader";
import { Big5Tabs } from "./verdict/Big5Tabs";
import { QuestionCard } from "./verdict/QuestionCard";
import { updateOnboardingStatus } from "@/services/onboardingService";

/** VB-F1 + VB-F2 · the analysis verdict. Lands here from the run screen (on
 *  DONE), the analyses history, and the create wizard (step 3 of 3).
 *
 *  Branches on the shadow status: a still-running analysis bounces to the run
 *  screen, FAILED shows an error, DONE renders the verdict (header + Big-5 tabs
 *  + per-question cards with receipts). All summaries/signals/mentions derive
 *  from the result envelope; the per-question summary prefers the LLM commentary
 *  and falls back to a deterministic sentence. */
export function VerdictPage() {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isCreateFlow = searchParams.get("new") === "1";
  const isOnboardingFlow = searchParams.get("onboarding") === "1";

  const buyerId = Number(params.buyerId);
  const analysisId = Number(params.analysisId);
  const validIds =
    Number.isInteger(buyerId) && buyerId > 0 && Number.isInteger(analysisId) && analysisId > 0;

  const buyer = useBuyer(validIds ? buyerId : undefined);
  const analysis = useAnalysis(validIds ? buyerId : undefined, validIds ? analysisId : undefined);

  const status = analysis.data?.status;
  const companyName = buyer.data?.company.name ?? "your company";
  const who = buyer.data?.name ?? "The buyer";

  // A non-terminal analysis belongs on the run screen - send the user there
  // (carry the create-flow flag so the wizard header stays consistent).
  useEffect(() => {
    if (!validIds) return;
    if (status === "PENDING" || status === "IN_PROGRESS") {
      const query = new URLSearchParams();
      if (isCreateFlow) query.set("new", "1");
      if (isOnboardingFlow) query.set("onboarding", "1");
      const suffix = query.size > 0 ? `?${query.toString()}` : "";
      navigate(`/virtual-buyer/${buyerId}/run/${analysisId}${suffix}`, { replace: true });
    }
  }, [status, validIds, buyerId, analysisId, isCreateFlow, isOnboardingFlow, navigate]);

  const [completingOnboarding, setCompletingOnboarding] = useState(false);
  const [onboardingFinalized, setOnboardingFinalized] = useState(false);
  const [onboardingCompleteError, setOnboardingCompleteError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const completeOnboarding = async () => {
      if (!isOnboardingFlow || !validIds || status !== "DONE") return;

      setCompletingOnboarding(true);
      setOnboardingCompleteError(null);
      try {
        await updateOnboardingStatus({
          step: "complete",
          buyer_id: buyerId,
          simulation_id: analysisId,
        });
        if (mounted) setOnboardingFinalized(true);
      } catch (err) {
        if (!mounted) return;
        setOnboardingCompleteError(extractVbError(err).detail);
      } finally {
        if (!mounted) return;
        setCompletingOnboarding(false);
      }
    };

    void completeOnboarding();

    return () => {
      mounted = false;
    };
  }, [isOnboardingFlow, validIds, status, buyerId, analysisId]);

  const model = useMemo(
    () =>
      status === "DONE"
        ? buildVerdictModel(analysis.data?.result as AnalysisResultEnvelope | null, companyName)
        : null,
    [status, analysis.data?.result, companyName],
  );

  const [activeCat, setActiveCat] = useState<Big5Category | null>(null);
  // Reconcile the selected tab against the current model: if a refetch drops
  // the selected category, fall back to the first one instead of an empty view.
  const activeIsPresent = !!activeCat && !!model?.categories.some((c) => c.category === activeCat);
  const active = (activeIsPresent ? activeCat : model?.categories[0]?.category) ?? null;

  // ---- error / loading / non-terminal ----
  if (!validIds || analysis.isError) {
    const err = analysis.isError ? extractVbError(analysis.error) : null;
    const transient = err != null && (err.status == null || err.status === 429 || err.status >= 500);
    return (
      <VbError
        title={transient ? "Couldn't reach the verdict" : "Verdict not found"}
        description={
          transient
            ? "We're having trouble reaching the analysis service. This is usually temporary - try again in a moment."
            : "We couldn't load this analysis. It may have been removed, or the link is wrong."
        }
        onRetry={validIds ? () => analysis.refetch() : undefined}
      />
    );
  }
  if (analysis.isPending) return <VerdictSkeleton />;

  const backHref = `/virtual-buyer/${buyerId}`;
  const crumb = (
    <Link className="vb-detail-crumb" to={backHref}>
      <ChevronLeft size={13} aria-hidden="true" />
      {buyer.data?.name ?? "Buyer"}
    </Link>
  );
  const createSteps = (
    <div className="steps" aria-label="Create buyer progress">
      <span className="step done">
        <span className="num">
          <Check size={12} aria-hidden="true" />
        </span>
        Set up buyer &amp; questions
      </span>
      <span className="sep" />
      <span className="step done">
        <span className="num">
          <Check size={12} aria-hidden="true" />
        </span>
        Run simulation
      </span>
      <span className="sep" />
      <span className="step active" aria-current="step">
        <span className="num">3</span>
        Review verdict
      </span>
    </div>
  );
  const topNav = isCreateFlow ? createSteps : crumb;

  if (status === "PENDING" || status === "IN_PROGRESS") {
    // The effect above redirects; render a brief placeholder for the one frame.
    return <VerdictSkeleton />;
  }

  if (status === "FAILED") {
    return (
      <div className="vb-detail">
        {topNav}
        <VbError
          title="This run didn't finish"
          description={
            analysis.data?.error ||
            "Something went wrong before the verdict was ready. You can run it again from the buyer."
          }
        />
      </div>
    );
  }

  if (!model || model.questions.length === 0 || model.categories.length === 0 || !active) {
    return (
      <div className="vb-detail">
        {topNav}
        <VbEmpty
          title="No verdict to show"
          description="This run completed but produced no judged answers. Try running the analysis again."
        />
      </div>
    );
  }

  const visible = model.questions.filter((q) => q.category === active);

  return (
    <div className="vb-verdict-page">
      {topNav}
      <VerdictHeader model={model} />
      <Big5Tabs categories={model.categories} active={active} onSelect={setActiveCat} />
      <p className="vb-tabs-hint">
        What AI told {who} across the models, by topic. Open any question to see the answers and the
        sources behind them.
      </p>
      <div
        id="vb-qcards-panel"
        role="tabpanel"
        tabIndex={0}
        aria-labelledby={`vb-tab-${active}`}
        className="vb-qcards"
      >
        {visible.map((q) => (
          <QuestionCard key={q.id} q={q} who={who} />
        ))}
      </div>

      <div className="vb-verdict-foot">
        {isOnboardingFlow ? (
          <>
            {onboardingCompleteError && (
              <div className="limit-flash show" role="alert">
                Could not finalize onboarding automatically: {onboardingCompleteError}
              </div>
            )}
            <button
              type="button"
              className="vb-btn vb-btn--primary vb-btn--block-mobile"
              onClick={() =>
                navigate("/", {
                  state: onboardingFinalized
                    ? {
                        onboardingCompleted: true,
                        onboardingCompanyId: buyer.data?.company.id ?? null,
                        onboardingCompanyName: buyer.data?.company.name ?? null,
                      }
                    : undefined,
                })
              }
              disabled={completingOnboarding}
            >
              {completingOnboarding ? "Finishing setup..." : "Go to dashboard"}
              <ArrowRight size={15} aria-hidden="true" />
            </button>
          </>
        ) : (
          <Link className="vb-btn vb-btn--primary vb-btn--block-mobile" to={backHref}>
            {isCreateFlow ? "Go to buyer" : "View buyer"}
            <ArrowRight size={15} aria-hidden="true" />
          </Link>
        )}
      </div>
    </div>
  );
}

export default VerdictPage;
